import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ThemeProvider,
  createTheme,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Popper,
  Paper as MuiPaper,
  useMediaQuery
} from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Home from './components/pages/Home';
import CampDetails from './components/pages/CampDetails';
import MobileCampView from './components/pages/MobileCampView';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#4caf50',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          textTransform: 'none',
          fontWeight: 500,
          padding: '6px 20px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  },
});

function App() {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [campTypes, setCampTypes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [open, setOpen] = useState(false);

  const handlePopoverOpen = (event, camp) => {
    setAnchorEl(event.currentTarget);
    setSelectedCamp(camp);
    setOpen(true);
  };

  const handlePopoverClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setSelectedCamp(null);
  };

  const navigateToProduct = (handle) => {
    window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${handle}`, '_blank');
  };

  const fetchCamps = async () => {
    setLoading(true);
    setError(null);
    try {
      const storeUrl = process.env.REACT_APP_SHOPIFY_STORE_URL;
      const accessToken = process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN;
      
      console.log('Environment Variables:', {
        storeUrl,
        hasAccessToken: !!accessToken
      });

      const graphqlQuery = `
        query {
          products(first: 50) {
            edges {
              node {
                id
                title
                description
                tags
                handle
                featuredImage {
                  url
                  altText
                }
                metafield(namespace: "custom", key: "week_availability") {
                  value
                }
                shortDescriptionMetafield: metafield(namespace: "custom", key: "short_description") {
                  value
                }
                variants(first: 1) {
                  edges {
                    node {
                      price {
                        amount
                      }
                      quantityAvailable
                      availableForSale
                      currentlyNotInStock
                    }
                  }
                }
              }
            }
          }
        }
      `;

      console.log('GraphQL Query:', graphqlQuery);
      
      const response = await axios({
        url: 'https://shop.guildhalllearning.com/api/2024-01/graphql.json',
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Content-Type': 'application/json',
        },
        data: {
          query: graphqlQuery
        }
      });

      console.log('API Response Status:', response.status);
      console.log('API Response Headers:', response.headers);
      console.log('API Response Data:', JSON.stringify(response.data, null, 2));
      
      if (!response.data) {
        throw new Error(`No data received from API. Status: ${response.status}`);
      }

      if (response.data.errors) {
        console.error('GraphQL Errors:', response.data.errors);
        throw new Error(`GraphQL Error: ${response.data.errors[0].message}`);
      }

      const products = response.data.data.products.edges
        .map(edge => {
          console.log('Processing product:', edge.node.title);
          console.log('Product tags:', edge.node.tags);
          
          let weekAvailability = [];
          let shortDescription = '';
          try {
            if (edge.node.metafield?.value) {
              const metafieldData = JSON.parse(edge.node.metafield.value);
              weekAvailability = metafieldData.weeks || [];
            }
            if (edge.node.shortDescriptionMetafield?.value) {
              shortDescription = edge.node.shortDescriptionMetafield.value;
            }
          } catch (e) {
            console.error('Error parsing metafield data for product:', edge.node.title, e);
          }

          const variant = edge.node.variants.edges[0]?.node;
          return {
            id: edge.node.id,
            title: edge.node.title,
            description: edge.node.description,
            shortDescription: shortDescription || edge.node.description,
            tags: edge.node.tags || [],
            price: variant?.price.amount,
            weeks: weekAvailability,
            handle: edge.node.handle,
            imageUrl: edge.node.featuredImage?.url,
            imageAlt: edge.node.featuredImage?.altText || edge.node.title,
            quantityAvailable: variant?.quantityAvailable,
            availableForSale: variant?.availableForSale,
            currentlyNotInStock: variant?.currentlyNotInStock
          };
        });

      console.log('Processed products:', products);
      
      // Filter to only show products with "summer camp" tag
      const summerCamps = products.filter(product => {
        const productTags = product.tags.map(tag => tag.toLowerCase());
        console.log('Product:', product.title, 'Tags:', productTags);
        
        return productTags.includes('summer camp');
      });

      console.log('Filtered summer camps:', summerCamps);

      // Extract unique weeks and sort them
      const allWeeks = [...new Set(summerCamps.flatMap(p => 
        p.weeks.map(w => w.week)
      ))].sort((a, b) => parseInt(a) - parseInt(b));

      // Extract unique camp types from product titles
      const allCampTypes = [...new Set(summerCamps.map(p => p.title))].sort();

      console.log('Weeks:', allWeeks);
      console.log('Camp Types:', allCampTypes);

      setCamps(summerCamps);
      setWeeks(allWeeks);
      setCampTypes(allCampTypes);
    } catch (err) {
      console.error('API Error Details:', err);
      setError(`Error fetching camp data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCamps();
  }, []);

  useEffect(() => {
    // Send height to parent window
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'setHeight', height }, '*');
    };

    // Initial height send
    sendHeight();

    // Send height when content changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    resizeObserver.observe(document.body);

    // Send height on window resize
    window.addEventListener('resize', sendHeight);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'scrollTo') {
        window.scrollTo({
          top: event.data.position,
          behavior: 'smooth'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const getAvailabilityStatus = (product) => {
    if (!product.availableForSale || product.currentlyNotInStock) {
      return 'Sold Out';
    }
    if (product.quantityAvailable !== null && product.quantityAvailable <= 3) {
      return 'Almost Sold Out';
    }
    return 'Available';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return '#4caf50';
      case 'Almost Sold Out':
        return '#ff9800';
      case 'Sold Out':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getAvailabilityBadge = (camp) => {
    const status = getAvailabilityStatus(camp);
    const color = getStatusColor(status);
    
    return (
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'inline-block',
          color: 'white',
          backgroundColor: color,
          padding: '2px 8px',
          borderRadius: '12px',
          mt: 0.5,
          fontSize: '0.7rem',
          fontWeight: 'medium'
        }}
      >
        {status}
      </Typography>
    );
  };

  const isWeekAvailable = (camp, week) => {
    const weekInfo = camp.weeks.find(w => w.week === week.toString());
    return weekInfo?.available || false;
  };

  // Process camp data to add additional information for the details page
  const processedCamps = camps.map(camp => {
    const processedWeeks = camp.weeks.map(week => {
      // Add month names and determine if spots are limited
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const dateParts = week.dates.split('-');
      const month = monthNames[parseInt(dateParts[0].trim().split('/')[0]) - 1];
      
      // Randomly determine if spots are limited for demo purposes
      // In a real app, this would come from the API
      const limitedSpots = Math.random() > 0.5;
      const spotsLeft = limitedSpots ? Math.floor(Math.random() * 4) + 1 : null;
      
      return {
        ...week,
        month,
        limitedSpots,
        spotsLeft
      };
    });
    
    return {
      ...camp,
      weeks: processedWeeks
    };
  });

  // Check if the device is mobile
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <ThemeProvider theme={theme}>
      <Routes>
        <Route path="/" element={
          isMobile ? 
          <Navigate to="/mobile" replace /> :
          <Home 
            camps={processedCamps} 
            weeks={weeks} 
            campTypes={campTypes} 
            loading={loading} 
            error={error} 
            fetchCamps={fetchCamps}
            getAvailabilityBadge={getAvailabilityBadge}
            isWeekAvailable={isWeekAvailable}
          />
        } />
        <Route path="/camp/:handle" element={<CampDetails camps={processedCamps} />} />
        <Route path="/mobile" element={
          <MobileCampView 
            camps={processedCamps}
            loading={loading}
            error={error}
            fetchCamps={fetchCamps}
          />
        } />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
